<template>
  <v-container fluid class="pa-6">
    <v-card elevation="2">
      <!-- Cabeçalho -->
      <v-card-title class="bg-primary text-white d-flex align-center">
        <v-icon left class="mr-2">mdi-clock-time-four-outline</v-icon>
        <span class="text-h5">Relatório de Horas Trabalhadas</span>
      </v-card-title>

      <!-- Filtros -->
      <v-card-text class="pt-6">
        <v-form ref="form" @submit.prevent="gerarRelatorio">
          <v-row>
            <!-- Período -->
            <v-col cols="12" md="3">
              <v-text-field
                v-model="filtros.data_inicial"
                label="Data Inicial"
                type="date"
                outlined
                dense
                :rules="[v => !!v || 'Data inicial é obrigatória']"
                prepend-inner-icon="mdi-calendar"
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="3">
              <v-text-field
                v-model="filtros.data_final"
                label="Data Final"
                type="date"
                outlined
                dense
                :rules="[v => !!v || 'Data final é obrigatória']"
                prepend-inner-icon="mdi-calendar"
              ></v-text-field>
            </v-col>

            <!-- Tipo de Filtro -->
            <v-col cols="12" md="3">
              <v-select
                v-model="tipoFiltro"
                :items="tiposFiltro"
                label="Filtrar por"
                outlined
                dense
                prepend-inner-icon="mdi-filter"
              ></v-select>
            </v-col>

            <!-- Campo de Filtro Dinâmico -->
            <v-col cols="12" md="3">
              <v-text-field
                v-if="tipoFiltro === 'numero_pedido'"
                v-model="filtros.numero_pedido"
                label="Número do Pedido"
                outlined
                dense
                clearable
                prepend-inner-icon="mdi-file-document"
              ></v-text-field>

              <v-text-field
                v-else-if="tipoFiltro === 'numero_ordem'"
                v-model="filtros.numero_ordem"
                label="Número da Ordem SAP"
                outlined
                dense
                clearable
                prepend-inner-icon="mdi-file-document-outline"
              ></v-text-field>

              <v-text-field
                v-else-if="tipoFiltro === 'id_ordem'"
                v-model="filtros.id_ordem"
                label="ID da Ordem"
                type="number"
                outlined
                dense
                clearable
                prepend-inner-icon="mdi-identifier"
              ></v-text-field>

              <v-combobox
                v-else-if="tipoFiltro === 'id_ordens'"
                v-model="filtros.id_ordens"
                label="IDs das Ordens"
                outlined
                dense
                multiple
                chips
                small-chips
                deletable-chips
                prepend-inner-icon="mdi-format-list-numbered"
                hint="Digite o ID e pressione Enter"
                persistent-hint
              ></v-combobox>
            </v-col>
          </v-row>

          <!-- Filtro de Funcionário (Sempre visível - AND) -->
          <v-row>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="filtros.id_funcionario"
                :items="funcionarios"
                :loading="loadingFuncionarios"
                :search-input.sync="searchFuncionario"
                item-title="nome"
                item-value="id"
                label="Filtrar por Funcionário (Opcional)"
                outlined
                dense
                clearable
                prepend-inner-icon="mdi-account"
                hint="Combina com o filtro acima (AND)"
                persistent-hint
              >
                <template v-slot:item="{ props, item }">
                  <v-list-item v-bind="props">
                    <v-list-item-title>{{ item.raw.nome }}</v-list-item-title>
                    <v-list-item-subtitle>ID: {{ item.raw.id }} | Setor: {{ item.raw.setor_nome || 'N/A' }}</v-list-item-subtitle>
                  </v-list-item>
                </template>
                <template v-slot:selection="{ item }">
                  {{ item.raw.nome }} (ID: {{ item.raw.id }})
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>

          <!-- Botões -->
          <v-row>
            <v-col cols="12" class="d-flex gap-2">
              <v-btn
                color="primary"
                type="submit"
                :loading="loading"
                :disabled="loading"
              >
                <v-icon left>mdi-magnify</v-icon>
                Gerar Relatório
              </v-btn>

              <v-btn
                color="secondary"
                outlined
                @click="limparFiltros"
                :disabled="loading"
              >
                <v-icon left>mdi-refresh</v-icon>
                Limpar
              </v-btn>

              <v-spacer></v-spacer>

              <v-btn
                v-if="relatorio"
                color="success"
                @click="exportarExcel"
                :disabled="loading"
              >
                <v-icon left>mdi-file-excel</v-icon>
                Exportar Excel
              </v-btn>

              <v-btn
                v-if="relatorio"
                color="error"
                @click="gerarPDF"
                :disabled="loading"
              >
                <v-icon left>mdi-file-pdf-box</v-icon>
                Gerar PDF
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>

    <!-- Resultados -->
    <v-card v-if="relatorio" elevation="2" class="mt-6">
      <!-- Resumo Geral -->
      <v-card-title class="bg-grey-lighten-3">
        <v-icon left>mdi-chart-box</v-icon>
        Resumo Geral
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="2">
            <v-card outlined>
              <v-card-text class="text-center">
                <div class="text-h6 text-primary">{{ relatorio.total_ordens }}</div>
                <div class="text-caption">Total de Ordens</div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="2">
            <v-card outlined>
              <v-card-text class="text-center">
                <div class="text-h6 text-success">{{ relatorio.total_horas_trabalhadas }}</div>
                <div class="text-caption">Horas Trabalhadas</div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="2">
            <v-card outlined>
              <v-card-text class="text-center">
                <div class="text-h6 text-warning">{{ calcularMinutosTotais() }}</div>
                <div class="text-caption">Total em Minutos</div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="2">
            <v-card outlined>
              <v-card-text class="text-center">
                <div class="text-h6 text-purple">{{ calcularMediaFuncionarios() }}</div>
                <div class="text-caption">Média de Funcionários</div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="2">
            <v-card outlined>
              <v-card-text class="text-center">
                <div class="text-h6 text-info">{{ relatorio.periodo.data_inicial }}</div>
                <div class="text-caption">Data Inicial</div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="2">
            <v-card outlined>
              <v-card-text class="text-center">
                <div class="text-h6 text-info">{{ relatorio.periodo.data_final }}</div>
                <div class="text-caption">Data Final</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Detalhamento por Ordem -->
      <v-divider></v-divider>

      <v-card-title class="bg-grey-lighten-3 d-flex align-center">
        <v-icon left>mdi-format-list-bulleted</v-icon>
        Detalhamento por Ordem
        <v-spacer></v-spacer>
        <v-btn-toggle v-model="ordenacao" mandatory dense>
          <v-btn value="padrao" small>
            <v-icon left small>mdi-sort</v-icon>
            Padrão
          </v-btn>
          <v-btn value="minutos_asc" small>
            <v-icon left small>mdi-sort-numeric-ascending</v-icon>
            Minutos ↑
          </v-btn>
          <v-btn value="minutos_desc" small>
            <v-icon left small>mdi-sort-numeric-descending</v-icon>
            Minutos ↓
          </v-btn>
        </v-btn-toggle>
      </v-card-title>

      <v-card-text>
        <v-expansion-panels v-model="panelAberto" multiple>
          <v-expansion-panel
            v-for="(ordem, index) in ordensOrdenadas"
            :key="index"
          >
            <!-- Cabeçalho da Ordem -->
            <v-expansion-panel-title>
              <v-row no-gutters align="center">
                <v-col cols="3">
                  <div class="font-weight-bold">{{ ordem.numero_ordem }}</div>
                  <div class="text-caption text-grey">ID: {{ ordem.id_ordem }}</div>
                </v-col>
                <v-col cols="3">
                  <div class="text-body-2">{{ ordem.modelo }}</div>
                  <div class="text-caption text-grey">{{ ordem.cliente }}</div>
                </v-col>
                <v-col cols="2">
                  <v-chip color="primary" small>
                    {{ ordem.quantidade }} pçs
                  </v-chip>
                </v-col>
                <v-col cols="2">
                  <v-chip color="success" small>
                    <v-icon left small>mdi-clock</v-icon>
                    {{ ordem.horas_trabalhadas }}
                  </v-chip>
                </v-col>
                <v-col cols="2">
                  <v-chip color="warning" small>
                    <v-icon left small>mdi-timer</v-icon>
                    {{ converterHorasParaMinutos(ordem.horas_trabalhadas) }} min
                  </v-chip>
                </v-col>
                <v-col cols="2" class="text-right">
                  <v-chip color="info" small>
                    <v-icon left small>mdi-account-multiple</v-icon>
                    {{ getTotalFuncionarios(ordem) }} func.
                  </v-chip>
                </v-col>
              </v-row>
            </v-expansion-panel-title>

            <!-- Conteúdo da Ordem -->
            <v-expansion-panel-text>
              <!-- Informações Gerais -->
              <v-row class="mb-4">
                <v-col cols="12" md="6">
                  <v-list dense>
                    <v-list-item>
                      <v-list-item-title>Data Lançamento:</v-list-item-title>
                      <v-list-item-subtitle>{{ formatarData(ordem.data_lancamento) }}</v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-title>Previsão Entrega:</v-list-item-title>
                      <v-list-item-subtitle>{{ formatarData(ordem.previsao_entrega) }}</v-list-item-subtitle>
                    </v-list-item>
                  </v-list>
                </v-col>
              </v-row>

              <!-- Detalhamento por Data -->
              <v-divider class="my-4"></v-divider>
              <div class="text-subtitle-1 font-weight-bold mb-3">
                <v-icon left>mdi-calendar-clock</v-icon>
                Detalhamento por Data
              </div>

              <v-expansion-panels>
                <v-expansion-panel
                  v-for="(detalhe, idx) in ordem.detalhamento_por_data"
                  :key="idx"
                >
                  <v-expansion-panel-title>
                    <v-row no-gutters align="center">
                      <v-col cols="3">
                        <strong>{{ formatarData(detalhe.data) }}</strong>
                      </v-col>
                      <v-col cols="3" class="text-center">
                        <v-chip color="success" x-small>
                          <v-icon left x-small>mdi-clock</v-icon>
                          {{ detalhe.horas_trabalhadas }}
                        </v-chip>
                      </v-col>
                      <v-col cols="3" class="text-center">
                        <v-chip color="warning" x-small>
                          <v-icon left x-small>mdi-timer</v-icon>
                          {{ converterHorasParaMinutos(detalhe.horas_trabalhadas) }} min
                        </v-chip>
                      </v-col>
                      <v-col cols="3" class="text-center">
                        <v-chip color="info" x-small>
                          <v-icon left x-small>mdi-account-multiple</v-icon>
                          {{ detalhe.quantidade_funcionarios }} func.
                        </v-chip>
                      </v-col>
                    </v-row>
                  </v-expansion-panel-title>

                  <v-expansion-panel-text v-if="detalhe.registros_detalhados && detalhe.registros_detalhados.length > 0">
                    <!-- Filtro de Funcionário -->
                    <v-text-field
                      v-model="filtroFuncionario"
                      label="Filtrar Funcionário"
                      prepend-inner-icon="mdi-magnify"
                      dense
                      outlined
                      clearable
                      class="mb-3"
                      hide-details
                    ></v-text-field>

                    <v-data-table
                      :headers="headersDetalhamento"
                      :items="detalhe.registros_detalhados"
                      :search="filtroFuncionario"
                      :items-per-page="10"
                      :footer-props="{
                        'items-per-page-options': [5, 10, 20, 50, -1]
                      }"
                      dense
                      class="elevation-1"
                    >
                      <template v-slot:item.id_funcionario="{ item }">
                        <v-chip x-small color="primary">
                          {{ item.id_funcionario }}
                        </v-chip>
                      </template>

                      <template v-slot:item.nome_funcionario="{ item }">
                        <strong>{{ item.nome_funcionario }}</strong>
                      </template>

                      <template v-slot:item.inicio_at="{ item }">
                        {{ formatarHora(item.inicio_at) }}
                      </template>

                      <template v-slot:item.fim_at="{ item }">
                        {{ formatarHora(item.fim_at) }}
                      </template>

                      <template v-slot:item.tempo_trabalhado_formatado="{ item }">
                        <v-chip x-small color="success">
                          {{ item.tempo_trabalhado_formatado }}
                        </v-chip>
                      </template>

                      <template v-slot:item.minutos="{ item }">
                        <v-chip x-small color="warning">
                          {{ Math.round(item.tempo_trabalhado_segundos / 60) }}
                        </v-chip>
                      </template>
                    </v-data-table>

                    <!-- Total da Data -->
                    <v-card outlined class="mt-3 pa-3">
                      <v-row>
                        <v-col cols="6">
                          <strong>Total de Funcionários:</strong> {{ detalhe.quantidade_funcionarios }}
                        </v-col>
                        <v-col cols="6" class="text-right">
                          <strong>Total em Minutos:</strong> 
                          <v-chip small color="primary">
                            {{ converterHorasParaMinutos(detalhe.horas_trabalhadas) }} min
                          </v-chip>
                        </v-col>
                      </v-row>
                    </v-card>
                  </v-expansion-panel-text>

                  <v-expansion-panel-text v-else>
                    <v-alert type="info" dense>
                      Nenhum registro detalhado disponível para esta data.
                    </v-alert>
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>

              <!-- Tabela Resumo (Antiga) -->
              <v-simple-table dense class="mt-4">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Data</th>
                      <th class="text-center">Horas Trabalhadas</th>
                      <th class="text-center">Minutos</th>
                      <th class="text-center">Funcionários</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(detalhe, idx) in ordem.detalhamento_por_data"
                      :key="'resumo-' + idx"
                    >
                      <td>{{ formatarData(detalhe.data) }}</td>
                      <td class="text-center">
                        <v-chip color="success" x-small>
                          {{ detalhe.horas_trabalhadas }}
                        </v-chip>
                      </td>
                      <td class="text-center">
                        <v-chip color="warning" x-small>
                          {{ converterHorasParaMinutos(detalhe.horas_trabalhadas) }}
                        </v-chip>
                      </td>
                      <td class="text-center">
                        <v-chip color="info" x-small>
                          {{ detalhe.quantidade_funcionarios }}
                        </v-chip>
                      </td>
                    </tr>
                    <!-- Total da Ordem -->
                    <tr class="bg-grey-lighten-4 font-weight-bold">
                      <td>TOTAL</td>
                      <td class="text-center">
                        <v-chip color="primary" small>
                          {{ ordem.horas_trabalhadas }}
                        </v-chip>
                      </td>
                      <td class="text-center">
                        <v-chip color="primary" small>
                          {{ converterHorasParaMinutos(ordem.horas_trabalhadas) }}
                        </v-chip>
                      </td>
                      <td class="text-center">
                        <v-chip color="primary" small>
                          {{ getTotalFuncionarios(ordem) }}
                        </v-chip>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>
    </v-card>

    <!-- Mensagem de Nenhum Resultado -->
    <v-card v-else-if="!loading && buscaRealizada" elevation="2" class="mt-6">
      <v-card-text class="text-center py-12">
        <v-icon size="64" color="grey">mdi-file-document-outline</v-icon>
        <div class="text-h6 mt-4 text-grey">Nenhum resultado encontrado</div>
        <div class="text-caption text-grey">Tente ajustar os filtros e buscar novamente</div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { ref, reactive, computed } from 'vue';
import { useToast } from 'vue-toastification';
import api from '@/api/api';

export default {
  name: 'RelatorioHorasTrabalhadas',

  setup() {
    const toast = useToast();
    const form = ref(null);
    const loading = ref(false);
    const buscaRealizada = ref(false);
    const relatorio = ref(null);
    const panelAberto = ref([]);
    const ordenacao = ref('padrao');

    const tipoFiltro = ref('numero_pedido');
    const tiposFiltro = [
      { title: 'Número do Pedido', value: 'numero_pedido' },
      { title: 'Número da Ordem', value: 'numero_ordem' },
      { title: 'ID da Ordem', value: 'id_ordem' },
      { title: 'Lista de IDs', value: 'id_ordens' },
    ];

    const filtros = reactive({
      data_inicial: '',
      data_final: '',
      numero_pedido: '',
      numero_ordem: '',
      id_ordem: null,
      id_ordens: [],
      id_funcionario: null,
    });

    const funcionarios = ref([]);
    const loadingFuncionarios = ref(false);
    const searchFuncionario = ref('');
    const filtroFuncionario = ref('');

    // Headers para a tabela de detalhamento
    const headersDetalhamento = [
      { text: 'ID', value: 'id_funcionario', align: 'center', sortable: true },
      { text: 'Funcionário', value: 'nome_funcionario', align: 'left', sortable: true },
      { text: 'Início', value: 'inicio_at', align: 'center', sortable: true },
      { text: 'Fim', value: 'fim_at', align: 'center', sortable: true },
      { text: 'Duração (HH:MM:SS)', value: 'tempo_trabalhado_formatado', align: 'center', sortable: false },
      { text: 'Minutos', value: 'minutos', align: 'center', sortable: true },
    ];

    // Inicializar datas (último mês)
    const hoje = new Date();
    const mesPassado = new Date(hoje);
    mesPassado.setMonth(mesPassado.getMonth() - 1);
    filtros.data_inicial = mesPassado.toISOString().split('T')[0];
    filtros.data_final = hoje.toISOString().split('T')[0];

    // Buscar funcionários
    const buscarFuncionarios = async () => {
      loadingFuncionarios.value = true;
      try {
        const response = await api.get('/funcionarios-lista');
        if (response.data.success) {
          funcionarios.value = response.data.data;
        }
      } catch (error) {
        console.error('Erro ao buscar funcionários:', error);
        toast.error('Erro ao carregar lista de funcionários');
      } finally {
        loadingFuncionarios.value = false;
      }
    };

    // Carregar funcionários ao montar o componente
    buscarFuncionarios();

    const gerarRelatorio = async () => {
      if (!form.value.validate()) {
        return;
      }

      loading.value = true;
      buscaRealizada.value = false;

      try {
        const payload = {
          data_inicial: filtros.data_inicial,
          data_final: filtros.data_final,
        };

        // Adicionar filtro selecionado
        if (tipoFiltro.value === 'numero_pedido' && filtros.numero_pedido) {
          payload.numero_pedido = filtros.numero_pedido;
        } else if (tipoFiltro.value === 'numero_ordem' && filtros.numero_ordem) {
          payload.numero_ordem = filtros.numero_ordem;
        } else if (tipoFiltro.value === 'id_ordem' && filtros.id_ordem) {
          payload.id_ordem = parseInt(filtros.id_ordem);
        } else if (tipoFiltro.value === 'id_ordens' && filtros.id_ordens.length > 0) {
          payload.id_ordens = filtros.id_ordens.map(id => parseInt(id));
        }

        // Filtro de funcionário é SEMPRE aplicado se preenchido (AND)
        if (filtros.id_funcionario) {
          payload.id_funcionario = parseInt(filtros.id_funcionario);
        }

        const response = await api.post('/relatorio-horas-trabalhadas', payload);

        if (response.data.success) {
          relatorio.value = response.data;
          buscaRealizada.value = true;
          toast.success(response.data.message);
          
          // Abrir todos os painéis por padrão
          panelAberto.value = relatorio.value.ordens.map((_, index) => index);
        } else {
          toast.warning(response.data.message);
          relatorio.value = null;
          buscaRealizada.value = true;
        }
      } catch (error) {
        console.error('Erro ao gerar relatório:', error);
        toast.error(error.response?.data?.message || 'Erro ao gerar relatório');
        relatorio.value = null;
        buscaRealizada.value = true;
      } finally {
        loading.value = false;
      }
    };

    const limparFiltros = () => {
      filtros.numero_pedido = '';
      filtros.numero_ordem = '';
      filtros.id_ordem = null;
      filtros.id_ordens = [];
      filtros.id_funcionario = null;
      relatorio.value = null;
      buscaRealizada.value = false;
    };

    const formatarData = (data) => {
      if (!data) return 'N/A';
      const [ano, mes, dia] = data.split('-');
      return `${dia}/${mes}/${ano}`;
    };

    const formatarHora = (dataHora) => {
      if (!dataHora) return 'N/A';
      const data = new Date(dataHora);
      const horas = String(data.getHours()).padStart(2, '0');
      const minutos = String(data.getMinutes()).padStart(2, '0');
      const segundos = String(data.getSeconds()).padStart(2, '0');
      return `${horas}:${minutos}:${segundos}`;
    };

    const calcularDuracao = (inicio, fim) => {
      if (!inicio) return 'N/A';
      const dataInicio = new Date(inicio);
      const dataFim = fim ? new Date(fim) : new Date();
      const diffMs = dataFim - dataInicio;
      
      if (diffMs < 0) return '00:00:00';
      
      const horas = Math.floor(diffMs / 3600000);
      const minutos = Math.floor((diffMs % 3600000) / 60000);
      const segundos = Math.floor((diffMs % 60000) / 1000);
      
      return `${String(horas).padStart(2, '0')}:${String(minutos).padStart(2, '0')}:${String(segundos).padStart(2, '0')}`;
    };

    const getTotalFuncionarios = (ordem) => {
      if (!ordem.detalhamento_por_data || ordem.detalhamento_por_data.length === 0) {
        return 0;
      }
      
      // Somar funcionários únicos de todas as datas
      const funcionariosUnicos = new Set();
      ordem.detalhamento_por_data.forEach(detalhe => {
        for (let i = 0; i < detalhe.quantidade_funcionarios; i++) {
          funcionariosUnicos.add(`${detalhe.data}-${i}`);
        }
      });
      
      return Math.max(...ordem.detalhamento_por_data.map(d => d.quantidade_funcionarios));
    };

    // Converter formato HH:MM:SS para minutos
    const converterHorasParaMinutos = (horasStr) => {
      if (!horasStr) return 0;
      
      const partes = horasStr.split(':');
      if (partes.length !== 3) return 0;
      
      const horas = parseInt(partes[0]) || 0;
      const minutos = parseInt(partes[1]) || 0;
      const segundos = parseInt(partes[2]) || 0;
      
      return Math.round(horas * 60 + minutos + segundos / 60);
    };

    // Calcular total de minutos do relatório
    const calcularMinutosTotais = () => {
      if (!relatorio.value || !relatorio.value.ordens) return 0;
      
      const totalMinutos = relatorio.value.ordens.reduce((total, ordem) => {
        return total + converterHorasParaMinutos(ordem.horas_trabalhadas);
      }, 0);
      
      return totalMinutos.toLocaleString('pt-BR');
    };

    // Calcular média de funcionários
    const calcularMediaFuncionarios = () => {
      if (!relatorio.value || !relatorio.value.ordens || relatorio.value.ordens.length === 0) {
        return '0';
      }
      
      const totalFuncionarios = relatorio.value.ordens.reduce((total, ordem) => {
        return total + getTotalFuncionarios(ordem);
      }, 0);
      
      const media = totalFuncionarios / relatorio.value.ordens.length;
      return media.toFixed(1);
    };

    // Ordenar ordens
    const ordensOrdenadas = computed(() => {
      if (!relatorio.value || !relatorio.value.ordens) return [];
      
      const ordens = [...relatorio.value.ordens];
      
      if (ordenacao.value === 'minutos_asc') {
        return ordens.sort((a, b) => {
          const minutosA = converterHorasParaMinutos(a.horas_trabalhadas);
          const minutosB = converterHorasParaMinutos(b.horas_trabalhadas);
          return minutosA - minutosB;
        });
      } else if (ordenacao.value === 'minutos_desc') {
        return ordens.sort((a, b) => {
          const minutosA = converterHorasParaMinutos(a.horas_trabalhadas);
          const minutosB = converterHorasParaMinutos(b.horas_trabalhadas);
          return minutosB - minutosA;
        });
      }
      
      return ordens;
    });

    const exportarExcel = () => {
      toast.info('Funcionalidade de exportação em desenvolvimento');
      // TODO: Implementar exportação para Excel
    };

    const gerarPDF = async () => {
      loading.value = true;

      try {
        const payload = {
          data_inicial: filtros.data_inicial,
          data_final: filtros.data_final,
        };

        // Adicionar filtro selecionado
        if (tipoFiltro.value === 'numero_pedido' && filtros.numero_pedido) {
          payload.numero_pedido = filtros.numero_pedido;
        } else if (tipoFiltro.value === 'numero_ordem' && filtros.numero_ordem) {
          payload.numero_ordem = filtros.numero_ordem;
        } else if (tipoFiltro.value === 'id_ordem' && filtros.id_ordem) {
          payload.id_ordem = parseInt(filtros.id_ordem);
        } else if (tipoFiltro.value === 'id_ordens' && filtros.id_ordens.length > 0) {
          payload.id_ordens = filtros.id_ordens.map(id => parseInt(id));
        }

        // Filtro de funcionário é SEMPRE aplicado se preenchido (AND)
        if (filtros.id_funcionario) {
          payload.id_funcionario = parseInt(filtros.id_funcionario);
        }

        const response = await api.post('/relatorio-horas-trabalhadas-pdf', payload, {
          responseType: 'blob'
        });

        // Criar URL do blob e fazer download
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `relatorio_horas_${new Date().toISOString().split('T')[0]}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);

        toast.success('PDF gerado com sucesso!');
      } catch (error) {
        console.error('Erro ao gerar PDF:', error);
        toast.error(error.response?.data?.message || 'Erro ao gerar PDF');
      } finally {
        loading.value = false;
      }
    };

    return {
      form,
      loading,
      buscaRealizada,
      relatorio,
      panelAberto,
      ordenacao,
      tipoFiltro,
      tiposFiltro,
      filtros,
      gerarRelatorio,
      limparFiltros,
      formatarData,
      formatarHora,
      calcularDuracao,
      getTotalFuncionarios,
      converterHorasParaMinutos,
      calcularMinutosTotais,
      calcularMediaFuncionarios,
      ordensOrdenadas,
      exportarExcel,
      gerarPDF,
      funcionarios,
      loadingFuncionarios,
      searchFuncionario,
      filtroFuncionario,
      headersDetalhamento,
    };
  },
};
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}

@media print {
  .v-btn,
  .v-expansion-panel-title__icon {
    display: none !important;
  }
  
  .v-expansion-panel-text {
    display: block !important;
  }
}
</style>
